import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/LanguageContext";
import { CALENDLY_URL } from "@/i18n";
import { scrollToId } from "@/utils";
import { Asterisk, EASE, MaskedLine, Reveal, Segments } from "@/components/Reveal";

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const ySec = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-white pt-28 lg:pt-40" data-testid="hero-section">
      <div className="mx-auto grid max-w-[1600px] gap-14 px-6 lg:grid-cols-12 lg:gap-10 lg:px-16">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-noom-line px-4 py-2"
            data-testid="hero-tag"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-noom-lilac" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-noom-muted">
              {t.hero.tag}
            </span>
          </motion.div>

          <h1
            data-testid="hero-main-title"
            className="mt-8 font-display text-[clamp(2.75rem,8.5vw,6.5rem)] font-semibold leading-[0.98] tracking-[-0.03em] text-noom-ink"
          >
            {t.hero.lines.map((line, i) => (
              <MaskedLine key={i} delay={0.25 + i * 0.14}>
                <Segments segments={line} />
              </MaskedLine>
            ))}
          </h1>

          <Reveal delay={0.75} className="mt-8 max-w-xl">
            <p className="text-base leading-relaxed text-noom-muted sm:text-lg" data-testid="hero-subtitle">
              {t.hero.sub}
            </p>
          </Reveal>

          <Reveal delay={0.9} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              data-testid="hero-book-call-button"
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-3 rounded-full bg-noom-ink px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-noom-lilac hover:text-noom-ink"
            >
              {t.hero.primary}
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 transition-colors duration-300 group-hover:bg-noom-ink/10">
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </a>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("#valore")}
              className="rounded-full border border-noom-line px-7 py-4 text-sm font-semibold text-noom-ink transition-colors duration-300 hover:border-noom-ink"
            >
              {t.hero.secondary}
            </button>
          </Reveal>
        </motion.div>

        <div className="relative hidden min-h-[560px] lg:col-span-5 lg:block">
          <motion.div
            style={{ y: ySec }}
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.55, ease: EASE }}
            className="absolute right-0 top-0 w-[44%]"
          >
            <img
              src="/assets/p-texture.jpg"
              alt="Texture visiva studio noom"
              className="aspect-[3/4] w-full object-cover"
            />
          </motion.div>
          <motion.div
            style={{ y: yMain }}
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.35, ease: EASE }}
            className="absolute left-0 top-24 w-[64%]"
          >
            <img
              src="/assets/hero-giorgia.jpg"
              alt="Giorgia di studio noom al lavoro su un'identità di brand"
              className="aspect-[4/5] w-full border border-noom-line object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="absolute bottom-10 right-6"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
              className="flex h-24 w-24 items-center justify-center rounded-full bg-noom-lilac"
            >
              <Asterisk className="h-9 w-9 text-noom-ink" />
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
          className="lg:hidden"
        >
          <img
            src="/assets/hero-giorgia.jpg"
            alt="Giorgia di studio noom al lavoro su un'identità di brand"
            className="aspect-[4/5] w-full border border-noom-line object-cover"
          />
        </motion.div>
      </div>

      <div className="mx-auto mt-16 max-w-[1600px] px-6 lg:mt-24 lg:px-16">
        <div className="flex items-center justify-between border-t border-noom-line py-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-noom-faint" data-testid="hero-meta">
            {t.hero.meta}
          </p>
          <p className="hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-noom-faint sm:flex">
            {t.hero.scroll}
            <motion.span animate={{ y: [0, 5, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
              <ArrowDown className="h-3.5 w-3.5 text-noom-lilacDeep" />
            </motion.span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
